import { RenderStage, type FieldCache } from './stage';
import { collectDimensions, applyDimension, valueFromUnit, type Dimension } from '../../bulk/dimensions';
import { scheduleFrame } from '../schedule';
import type { LoadedFont } from '../fonts/fontLoader';
import type { StudioDocument } from '../../store/studioStore';

/**
 * Frame sequencing for animation export.
 *
 * There are four honest ways to get motion out of a system whose output is a still
 * image, and each needs different machinery:
 *
 *  - **`build`** replays the growth. The technique's own `step` *is* the animation, so
 *    a frame is simply the composition after a fraction of the steps it takes to
 *    finish. The catch is that nothing knows the total step count in advance, which is
 *    why this mode measures first (see `prepare`).
 *  - **`live`** is for techniques that never finish — boids, fluid, physarum. One
 *    simulation, stepped by real time per frame.
 *  - **`sweep`** drives one parameter from A to B. Every frame is a full render, which
 *    is expensive, but it is the only mode where a *seamless* loop is possible: run the
 *    parameter out and back and the last frame equals the first.
 *  - **`seed`** redraws with a different seed each frame — the "boiling" look.
 *
 * Ping-pong is offered only where it is real. `build` and `live` are monotone
 * simulations; playing them backwards would require keeping every intermediate state,
 * which for a hundred frames of a particle system is hundreds of megabytes. Those modes
 * get a hold at the end instead.
 */
export type AnimationMode = 'build' | 'live' | 'sweep' | 'seed';

export type EasingId = 'linear' | 'easeInOut' | 'easeOut';

export const EASINGS: Record<EasingId, (t: number) => number> = {
  linear: (t) => t,
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2),
  easeOut: (t) => 1 - (1 - t) ** 3,
};

export interface AnimationSettings {
  mode: AnimationMode;
  fps: number;
  /** Seconds of motion, excluding the hold. */
  duration: number;
  scale: number;
  /** Seconds of the final image held at the end, so a loop rests before repeating. */
  holdSeconds: number;
  /** Sweep only: run the parameter out and back for a seamless loop. */
  pingPong: boolean;
  sweepDimensionId: string | null;
  /** Positions in the parameter's exploration range, as unit intervals. */
  sweepFrom: number;
  sweepTo: number;
  easing: EasingId;
}

export const DEFAULT_ANIMATION: AnimationSettings = {
  mode: 'build',
  fps: 24,
  duration: 3,
  scale: 1,
  holdSeconds: 0.6,
  pingPong: true,
  sweepDimensionId: null,
  sweepFrom: 0,
  sweepTo: 1,
  easing: 'linear',
};

export function supportsPingPong(mode: AnimationMode): boolean {
  return mode === 'sweep';
}

export function frameCountFor(settings: AnimationSettings): number {
  const motion = Math.max(1, Math.round(settings.duration * settings.fps));
  const hold = supportsPingPong(settings.mode) && settings.pingPong
    ? 0
    : Math.round(settings.holdSeconds * settings.fps);
  const doubled = supportsPingPong(settings.mode) && settings.pingPong ? motion * 2 - 2 : motion;
  return Math.max(1, doubled + hold);
}

/** Dimensions a sweep can drive — the same exploration ranges the studio shows. */
export function sweepDimensions(doc: StudioDocument): Dimension[] {
  return collectDimensions(doc, {
    layerIds: new Set(doc.layers.map((l) => l.id)),
    includeText: true,
    includeBackground: true,
    includePost: true,
  }).filter((d) => d.param.kind === 'float' || d.param.kind === 'int');
}

export interface AnimationFrame {
  index: number;
  total: number;
  /** Reused between frames — consume it before asking for the next one. */
  canvas: OffscreenCanvas | HTMLCanvasElement;
}

export interface SequencerOptions {
  doc: StudioDocument;
  font: LoadedFont;
  settings: AnimationSettings;
  /** Cap for the measuring pass and for `live`, where nothing ever completes. */
  maxSteps?: number;
  signal?: AbortSignal;
}

/** Hands the main thread back so the UI can paint between frames. */
function yieldToUi(): Promise<void> {
  return new Promise((resolve) => scheduleFrame(() => resolve()));
}

export class AnimationSequencer {
  readonly frameCount: number;
  readonly delayMs: number;
  readonly width: number;
  readonly height: number;

  /** Steps each layer needs to finish, filled in by `prepare` for `build`. */
  private stepBudget: number[] = [];
  private readonly fieldCache: FieldCache = new Map();
  private readonly maxSteps: number;

  constructor(private readonly options: SequencerOptions) {
    this.frameCount = frameCountFor(options.settings);
    this.delayMs = 1000 / options.settings.fps;
    this.width = Math.round(options.doc.canvas.width * options.settings.scale);
    this.height = Math.round(options.doc.canvas.height * options.settings.scale);
    this.maxSteps = options.maxSteps ?? 20000;
  }

  /**
   * Measures how long the build takes, so the frames can be spread across it.
   *
   * The measuring pass runs at scale 1 and never paints — it is the simulation only,
   * which for most techniques is a small fraction of the cost of the full render. The
   * alternative, guessing a fixed step budget, produces animations that either finish in
   * the first ten frames or never get going.
   */
  async prepare(onProgress?: (progress: number) => void): Promise<void> {
    if (this.options.settings.mode !== 'build') return;

    const probe = new RenderStage({ doc: this.options.doc, font: this.options.font, scale: 1 });
    this.stepBudget = probe.layers.map(() => 0);

    for (let i = 0; i < probe.layers.length; i++) {
      const entry = probe.layers[i];
      let steps = 0;
      while (steps < this.maxSteps && !entry.technique.isComplete(entry.state)) {
        entry.state = entry.technique.step(entry.state, 1 / 60, entry.context);
        steps += 1;
        // Long simulations would otherwise freeze the page for the whole probe.
        if (steps % 400 === 0) {
          if (this.options.signal?.aborted) return;
          await yieldToUi();
        }
      }
      this.stepBudget[i] = Math.max(1, steps);
      onProgress?.((i + 1) / probe.layers.length);
    }
  }

  async *frames(): AsyncGenerator<AnimationFrame> {
    const { settings } = this.options;
    switch (settings.mode) {
      case 'build':
        yield* this.buildFrames();
        return;
      case 'live':
        yield* this.liveFrames();
        return;
      default:
        yield* this.perFrameRenders();
    }
  }

  /** One stage, stepped a little further each frame. */
  private async *buildFrames(): AsyncGenerator<AnimationFrame> {
    const { settings } = this.options;
    const stage = new RenderStage({
      doc: this.options.doc,
      font: this.options.font,
      scale: settings.scale,
      fieldCache: this.fieldCache,
    });
    const ease = EASINGS[settings.easing];
    const motion = Math.max(1, Math.round(settings.duration * settings.fps));
    const done = stage.layers.map(() => 0);

    for (let i = 0; i < this.frameCount; i++) {
      if (this.options.signal?.aborted) return;
      // Past the motion frames the composition simply holds, which is what makes a GIF
      // loop readable instead of restarting the instant it finishes.
      const t = ease(Math.min(1, i / Math.max(1, motion - 1)));

      stage.layers.forEach((entry, k) => {
        const budget = this.stepBudget[k] ?? this.maxSteps;
        const target = Math.round(budget * t);
        if (target > done[k]) {
          stage.advance(entry, target - done[k]);
          done[k] = target;
        }
      });

      stage.paint();
      yield { index: i, total: this.frameCount, canvas: stage.canvas };
      await yieldToUi();
    }
  }

  /** One stage, stepped by wall-clock time — for techniques that never finish. */
  private async *liveFrames(): AsyncGenerator<AnimationFrame> {
    const { settings } = this.options;
    const stage = new RenderStage({
      doc: this.options.doc,
      font: this.options.font,
      scale: settings.scale,
      fieldCache: this.fieldCache,
    });
    const dt = 1 / settings.fps;

    for (let i = 0; i < this.frameCount; i++) {
      if (this.options.signal?.aborted) return;
      for (const entry of stage.layers) stage.advance(entry, 1, dt);
      stage.paint();
      yield { index: i, total: this.frameCount, canvas: stage.canvas };
      await yieldToUi();
    }
  }

  /**
   * A full render per frame, for `sweep` and `seed`.
   *
   * The field cache is what makes this affordable: unless the sweep drives a text
   * parameter, every frame shares one SDF instead of rebuilding it.
   */
  private async *perFrameRenders(): AsyncGenerator<AnimationFrame> {
    const { settings } = this.options;
    const ease = EASINGS[settings.easing];
    const motion = Math.max(1, Math.round(settings.duration * settings.fps));
    const dimension =
      settings.mode === 'sweep' && settings.sweepDimensionId
        ? sweepDimensions(this.options.doc).find((d) => d.id === settings.sweepDimensionId)
        : undefined;

    let canvas: OffscreenCanvas | HTMLCanvasElement | undefined;

    for (let i = 0; i < this.frameCount; i++) {
      if (this.options.signal?.aborted) return;

      // Ping-pong is a triangle over the frame index: frame `2m − 2 − i` mirrors frame
      // `i`, so the last frame is the one before the first and the loop has no seam.
      const raw =
        settings.pingPong && supportsPingPong(settings.mode)
          ? (i < motion ? i : 2 * motion - 2 - i)
          : Math.min(i, motion - 1);
      const t = ease(raw / Math.max(1, motion - 1));

      const doc = structuredClone(this.options.doc);
      if (settings.mode === 'seed') {
        // A deterministic sequence rather than random draws, so re-exporting the same
        // animation produces the same file.
        const step = Math.round(t * (motion - 1));
        doc.seed = (this.options.doc.seed + step * 7919) >>> 0;
        for (const layer of doc.layers) layer.seed = (layer.seed + step * 7919) >>> 0;
      } else if (dimension) {
        applyDimension(
          doc,
          dimension,
          valueFromUnit(dimension, settings.sweepFrom + (settings.sweepTo - settings.sweepFrom) * t),
        );
      }

      const stage = new RenderStage({
        doc,
        font: this.options.font,
        scale: settings.scale,
        fieldCache: this.fieldCache,
        canvas,
      });
      canvas = stage.canvas;
      stage.runToCompletion(this.maxSteps);
      stage.paint();

      yield { index: i, total: this.frameCount, canvas: stage.canvas };
      await yieldToUi();
    }
  }
}
